import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Button,
  ButtonColor,
  ButtonState,
} from '@delmonicos/shared-design-system-react/src/components/button/button';
import { CopyToClipboard } from '@delmonicos/shared-design-system-react/src/components/copy-to-clipboard/copy-to-clipboard';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { Picto } from '@delmonicos/shared-design-system-react/src/components/picto/picto';
import { Spinner } from '@delmonicos/shared-design-system-react/src/components/spinner/spinner';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import {
  Text,
  TextColor,
  TextFontSize,
  TextWeight,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { isSocketActive } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/connector-status.helper';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoUnlockConnectorOcppMutation } from '@app/core/service/cpms-bo-charge-point-ocpp.service';
import * as globalCss from '@shared/styles/global.module.scss';

import { ChargePointChannelDeleteController } from '../charge-point-channel-delete/charge-point-channel-delete.controller';
import { ChargePointChannelPhysicalSocketCreateController } from '../charge-point-channel-physical-socket-create/charge-point-channel-physical-socket-create.controller';
import { ChargePointChannelPhysicalSocketDetailsController } from '../charge-point-channel-physical-socket-details/charge-point-channel-physical-socket-details.controller';
import { ChargePointChannelTransactionDetailsPage } from '../charge-point-channel-transaction-details/charge-point-channel-transaction-details.controller';
import { ChargePointChannelUpdateController } from '../charge-point-channel-update/charge-point-channel-update.controller';
import * as css from '../charge-point-details/charge-point-details.module.scss';
import {
  ChargePointChannelPhysicalSocketContextProvider,
  useChannelEditModeContext,
  useChargePointChannelContext,
  useChargePointContext,
} from '../charge-point.context';
import { mapStatusToGenericConnectorStatus } from '../helper/connector-status.helper';

export interface ChargePointChannelDetailsControllerProps {
  highlighted?: boolean;
}

export const ChargePointChannelDetailsController = ({
  highlighted = false,
}: ChargePointChannelDetailsControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const chargePoint = useChargePointContext();
  const channel = useChargePointChannelContext();
  const { isEditMode } = useChannelEditModeContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canUpdateChannel = useMemo(
    () =>
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.CHANNELS,
        action: ActionsByResource.CHANNELS.UPDATE,
      }),
    [userPermissions],
  );

  const canDeleteChannel = useMemo(
    () =>
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.CHANNELS,
        action: ActionsByResource.CHANNELS.DELETE,
      }),
    [userPermissions],
  );

  // ACTIONS: UPDATE RESOURCE //////////////////////////////////////////////////

  const [actionUpdateIsDialogOpen, setActionUpdateIsDialogOpen] =
    useState(false);

  const [
    actionCreatePhysicalSocketIsDialogOpen,
    setActionCreatePhysicalSocketIsDialogOpen,
  ] = useState(false);

  // ACTIONS: DELETE RESOURCE //////////////////////////////////////////////////

  const [actionDeleteIsDialogOpen, setActionDeleteIsDialogOpen] =
    useState(false);

  // ACTIONS: UNLOCK TRANSACTION OCPP //////////////////////////////////////////

  const [
    actionUnlockConnectorIsDialogOpen,
    setActionUnlockConnectorIsDialogOpen,
  ] = useState(false);

  const [
    handleUnlockConnectorOcpp,
    {
      isError: isUnlockConnectorOcppError,
      isSuccess: isUnlockConnectorOcppSuccess,
      reset: resetUnlockConnectorOcpp,
    },
  ] = useCpmsBoUnlockConnectorOcppMutation({
    fixedCacheKey: `/charge-points/${chargePoint.id}/${channel.id}/ocpp/connector/unlock`,
  });

  useEffect(() => {
    if (isUnlockConnectorOcppError) {
      toast.error(t('$.charge-point-channel-actions.unlock-connector-error'));
      resetUnlockConnectorOcpp();
    }
    if (isUnlockConnectorOcppSuccess) {
      toast.success(
        t('$.charge-point-channel-actions.unlock-connector-success'),
      );
      resetUnlockConnectorOcpp();
    }
  }, [
    isUnlockConnectorOcppError,
    isUnlockConnectorOcppSuccess,
    resetUnlockConnectorOcpp,
    t,
  ]);

  // PHYSICAL SOCKET ///////////////////////////////////////////////////////////

  const isDeleteChannelDisabled = useMemo(
    () =>
      channel.physicalSockets?.some((socket) =>
        isSocketActive(socket.ocppStatus),
      ) ?? false,
    [channel.physicalSockets],
  );

  const computedStatus = mapStatusToGenericConnectorStatus(
    channel?.computedStatus,
  );
  const physicalSocketFirst = channel?.physicalSockets.at(0);

  if (!physicalSocketFirst) {
    return <div />;
  }

  // RENDER ////////////////////////////////////////////////////////////////////

  if (!channel) {
    return <Spinner />; // TODO: skeleton card instead
  }

  return (
    <>
      <Stack
        className={classNames(
          css.channelCard,
          highlighted && css.channelCardHighlighted,
        )}
        direction="column"
        gap={GapEnum.M}>
        {isEditMode && canDeleteChannel && (
          <Stack gap={GapEnum.S} direction="column">
            <ButtonIcon
              color={ButtonColor.Destructive}
              icon="trash-01"
              state={
                isDeleteChannelDisabled
                  ? ButtonState.Disabled
                  : ButtonState.Default
              }
              disabled={isDeleteChannelDisabled}
              onClick={() => {
                setActionDeleteIsDialogOpen(true);
              }}
            />
            <Divider />
          </Stack>
        )}
        <Stack
          alignItems="center"
          justifyContent="space-between"
          gap={GapEnum.XS}>
          <Stack
            className={css.qrCodeSection}
            alignItems="center"
            gap={GapEnum.XS}
            justifyContent="start">
            {/*
              TODO: add connectorId slot with dot indicator
            */}
            <Stack direction="column" gap={GapEnum.XXS}>
              <Text fontSize={TextFontSize.Small}>
                {t(
                  'delmonicos-shared-sdk.outputs-channel.qr-code',
                ).toUpperCase()}
              </Text>
              <Text
                fontSize={TextFontSize.Large}
                fontWeight={TextWeight.SemiBold}>
                {channel.qrCode ?? '-'}
              </Text>
            </Stack>
          </Stack>
          <Stack alignItems="center" justifyContent="end" gap={GapEnum.M}>
            {isEditMode && canUpdateChannel && (
              <ButtonIcon
                icon="edit-05"
                onClick={() => {
                  setActionUpdateIsDialogOpen(true);
                }}
              />
            )}
          </Stack>
        </Stack>

        <Stack gap={GapEnum.S}>
          <Text
            uppercase
            fontSize={TextFontSize.Small}
            color={TextColor.Secondary}>
            {t('delmonicos-shared-sdk.outputs-channel.ocpi-evse-id')}
          </Text>
          <CopyToClipboard textToCopy={channel.ocpiEvseId}>
            <Text
              fontSize={TextFontSize.Small}
              color={TextColor.Secondary}
              truncate>
              {channel.ocpiEvseId ?? '-'}
            </Text>
          </CopyToClipboard>
        </Stack>

        <Tag
          className={classNames(computedStatus.color, css.computedStatusTag)}
          gap={GapEnum.XXS}
          key="state">
          <Picto name={computedStatus.picto} />
          <Text>
            {t(
              `delmonicos-shared-sdk.ocpp-enums-charge-point-status.${channel.computedStatus}`,
            )}
          </Text>
        </Tag>
        {channel.physicalSockets.map((physicalSocket) => (
          <ChargePointChannelPhysicalSocketContextProvider
            key={physicalSocket.id}
            data={physicalSocket}>
            <ChargePointChannelPhysicalSocketDetailsController
              handleUnlockPhysicalSocket={() =>
                setActionUnlockConnectorIsDialogOpen(true)
              }
            />
          </ChargePointChannelPhysicalSocketContextProvider>
        ))}
        {!isEditMode && (
          <Stack
            direction="column"
            justifyContent="flex-end"
            flex={1}
            gap={GapEnum.M}>
            <ChargePointChannelTransactionDetailsPage />
          </Stack>
        )}
        {isEditMode && (
          <Stack gap={GapEnum.M} className={globalCss.flexedItems}>
            <ButtonIcon
              icon="plus"
              onClick={() => setActionCreatePhysicalSocketIsDialogOpen(true)}
            />
          </Stack>
        )}
      </Stack>
      <Dialog
        onClose={() => setActionUnlockConnectorIsDialogOpen(false)}
        open={actionUnlockConnectorIsDialogOpen}>
        <Dialog.Header
          title={t('$.charge-point-channel-actions.unlock-connector-title')}
          icon="wire"
        />
        <Dialog.Content>
          {t('$.charge-point-channel-actions.unlock-connector-message')}
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            color={ButtonColor.Secondary}
            onClick={() => setActionUnlockConnectorIsDialogOpen(false)}>
            {t('$.charge-point-channel-actions.unlock-connector-cancel')}
          </Button>
          <Button
            onClick={() => {
              handleUnlockConnectorOcpp({
                chargePointId: chargePoint.id,
                physicalSocketId: physicalSocketFirst.id,
              });
              setActionUnlockConnectorIsDialogOpen(false);
            }}>
            {t('$.charge-point-channel-actions.unlock-connector-confirm')}
          </Button>
        </Dialog.Actions>
      </Dialog>
      <Dialog
        onClose={() => setActionUpdateIsDialogOpen(false)}
        open={actionUpdateIsDialogOpen}>
        <Dialog.Header
          icon="electric-terminal"
          onClose={() => setActionUpdateIsDialogOpen(false)}
          title={t('$.charge-point-channel-update.title')}
        />
        <Dialog.Content>
          <ChargePointChannelUpdateController
            onCancel={() => setActionUpdateIsDialogOpen(false)}
            onSuccess={() => setActionUpdateIsDialogOpen(false)}
          />
        </Dialog.Content>
      </Dialog>
      <Dialog
        onClose={() => setActionDeleteIsDialogOpen(false)}
        open={actionDeleteIsDialogOpen}>
        <Dialog.Header
          icon="wire"
          onClose={() => setActionDeleteIsDialogOpen(false)}
          title={t('$.charge-point-channel-delete.title')}
        />
        <Dialog.Content>
          <ChargePointChannelDeleteController
            onCancel={() => setActionDeleteIsDialogOpen(false)}
            onSuccess={() => setActionDeleteIsDialogOpen(false)}
          />
        </Dialog.Content>
      </Dialog>
      <Dialog
        onClose={() => setActionCreatePhysicalSocketIsDialogOpen(false)}
        open={actionCreatePhysicalSocketIsDialogOpen}>
        <Dialog.Header
          icon="wire"
          onClose={() => setActionCreatePhysicalSocketIsDialogOpen(false)}
          title={t('$.charge-point-channel-physical-socket-create.title')}
        />
        <Dialog.Content>
          <ChargePointChannelPhysicalSocketCreateController
            onCancel={() => setActionCreatePhysicalSocketIsDialogOpen(false)}
            onSuccess={() => setActionCreatePhysicalSocketIsDialogOpen(false)}
          />
        </Dialog.Content>
      </Dialog>
    </>
  );
};
